import type { ReactNode } from 'react';
import {
  ArrowRightOutlined,
  AuditOutlined,
  BarChartOutlined,
  ControlOutlined,
  FundProjectionScreenOutlined,
  GlobalOutlined,
  LoginOutlined,
  RadarChartOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import './WelcomePage.css';

interface Capability {
  title: string;
  description: string;
  detail: string;
  icon: ReactNode;
}

const capabilities: Capability[] = [
  {
    title: '市场态势',
    description: '汇集山东现货价格、供需与气象信息，形成面向交易时点的统一观察面。',
    detail: '价格跟踪 · 供需研判 · 气象指引',
    icon: <RadarChartOutlined />,
  },
  {
    title: '交易决策',
    description: '贯通日前判断、策略测算与盘中跟踪，为申报和调整提供一致依据。',
    detail: '策略优化 · 情景测算 · 执行跟踪',
    icon: <FundProjectionScreenOutlined />,
  },
  {
    title: '资产运营',
    description: '协同光伏与储能运行信息，聚焦功率、限电、AGC 与储能策略执行。',
    detail: '光储协同 · 运行监控 · 异常定位',
    icon: <ControlOutlined />,
  },
  {
    title: '经营复盘',
    description: '连接结算、收益和策略表现，沉淀可追溯的经营分析与改进依据。',
    detail: '收益核算 · 结算校验 · 策略归因',
    icon: <AuditOutlined />,
  },
];

const workflow = ['市场数据', '交易判断', '执行监控', '结算复盘', '策略修正'];

const WelcomePage = () => {
  return (
    <main className="welcome-page">
      <section className="welcome-hero" aria-labelledby="welcome-title">
        <div
          className="welcome-hero-media"
          aria-hidden="true"
          style={{ backgroundImage: "url('/welcome/hero-control-room.jpg')" }}
        />
        <header className="welcome-header">
          <Link className="welcome-brand" to="/" aria-label="山东电力现货与运营综合中台首页">
            <span className="welcome-brand-mark" aria-hidden="true">
              <BarChartOutlined />
            </span>
            <span>GRID TRADE LAB · SD</span>
          </Link>
          <Link className="welcome-login" to="/login">
            <LoginOutlined aria-hidden="true" />
            <span>登录</span>
          </Link>
        </header>

        <div className="welcome-hero-content">
          <div className="welcome-hero-copy">
            <p className="welcome-kicker">SHANDONG POWER MARKET OPERATIONS</p>
            <h1 id="welcome-title">山东电力现货与运营综合中台</h1>
            <p className="welcome-hero-summary">
              面向光伏与储能交易运营，将公开市场信息、策略判断、执行监控与经营复盘汇于同一工作界面。
            </p>
            <div className="welcome-hero-meta" aria-label="平台覆盖范围">
              <span>山东现货</span>
              <span>光储协同</span>
              <span>经营闭环</span>
            </div>
          </div>
        </div>

        <a className="welcome-scroll-cue" href="#capabilities">
          <span>查看平台能力</span>
          <ArrowRightOutlined aria-hidden="true" />
        </a>
      </section>

      <section className="welcome-capabilities" id="capabilities" aria-labelledby="welcome-capabilities-title">
        <div className="welcome-section-inner">
          <div className="welcome-section-heading">
            <p className="welcome-section-index">01 / PLATFORM SCOPE</p>
            <div>
              <h2 id="welcome-capabilities-title">从市场观察到经营归因</h2>
              <p>围绕交易员的日常判断链路组织能力，让信息、决策与结果保持可追溯。</p>
            </div>
          </div>

          <div className="welcome-capability-grid">
            {capabilities.map((capability, index) => (
              <article className="welcome-capability" key={capability.title}>
                <div className="welcome-capability-topline">
                  <span className="welcome-capability-number">0{index + 1}</span>
                  <span className="welcome-capability-icon" aria-hidden="true">
                    {capability.icon}
                  </span>
                </div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
                <span className="welcome-capability-detail">{capability.detail}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="welcome-workflow" aria-labelledby="welcome-workflow-title">
        <div className="welcome-section-inner">
          <div className="welcome-workflow-heading">
            <p className="welcome-section-index welcome-section-index-light">02 / OPERATING LOOP</p>
            <h2 id="welcome-workflow-title">一条持续校正的业务闭环</h2>
          </div>
          <ol className="welcome-workflow-list">
            {workflow.map((step, index) => (
              <li className="welcome-workflow-step" key={step}>
                <span className="welcome-workflow-number">{String(index + 1).padStart(2, '0')}</span>
                <strong>{step}</strong>
                {index < workflow.length - 1 && <ArrowRightOutlined className="welcome-workflow-arrow" aria-hidden="true" />}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="welcome-evidence" aria-labelledby="welcome-evidence-title">
        <div className="welcome-section-inner">
          <div className="welcome-section-heading">
            <p className="welcome-section-index">03 / WORKING VIEWS</p>
            <div>
              <h2 id="welcome-evidence-title">让判断建立在同一份事实之上</h2>
              <p>以下界面均为公开演示环境的脱敏画面，仅用于说明平台的信息组织方式。</p>
            </div>
          </div>

          <figure className="welcome-view welcome-view-wide">
            <div className="welcome-view-copy">
              <span className="welcome-view-label">市场与气象</span>
              <h3>把天气影响落到交易时段</h3>
              <p>并列呈现预报、电力供需与价格线索，辅助识别光伏出力变化对现货交易的影响。</p>
            </div>
            <img
              src="/welcome/weather-guidance.png"
              alt="脱敏后的气象与交易指引平台界面"
              width="3840"
              height="2222"
              loading="lazy"
            />
          </figure>

          <div className="welcome-view-grid">
            <figure className="welcome-view">
              <img
                src="/welcome/storage-strategy.png"
                alt="脱敏后的储能策略测算平台界面"
                width="3840"
                height="2222"
                loading="lazy"
              />
              <figcaption className="welcome-view-copy">
                <span className="welcome-view-label">光储策略</span>
                <h3>从功率预测到执行方案</h3>
                <p>联动出力、限电与价格信息，支撑储能充放电策略的测算与执行跟踪。</p>
              </figcaption>
            </figure>
            <figure className="welcome-view">
              <img
                src="/welcome/storage-review.png"
                alt="脱敏后的储能收益复盘平台界面"
                width="3840"
                height="2222"
                loading="lazy"
              />
              <figcaption className="welcome-view-copy">
                <span className="welcome-view-label">经营复盘</span>
                <h3>让策略结果可解释</h3>
                <p>对照执行与收益结果，定位偏差来源，为下一交易周期留下可复用的修正依据。</p>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="welcome-builder" aria-labelledby="welcome-builder-title">
        <div className="welcome-section-inner welcome-builder-inner">
          <div>
            <p className="welcome-section-index">04 / BUILDER</p>
            <h2 id="welcome-builder-title">平台建设者</h2>
          </div>
          <div className="welcome-builder-copy">
            <p>由电力交易实践出发，持续把市场研究、交易方法和运营工具沉淀为可复用的数字化能力。</p>
            <a
              className="welcome-profile-link"
              href="https://yiping.gridtradelab.xyz"
              target="_blank"
              rel="noopener noreferrer"
            >
              <GlobalOutlined aria-hidden="true" />
              <span>查看个人主页</span>
              <ArrowRightOutlined aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      <footer className="welcome-footer">
        <span>山东电力现货与运营综合中台</span>
        <span>Grid Trade Lab · Shandong</span>
      </footer>
    </main>
  );
};

export default WelcomePage;
